import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconLock, IconLogin, IconQrcode, IconUserOff } from '@tabler/icons-react';
import { PROCEDURES_ERROR_CODES } from '@actschurch/ccms-sdk';
import { PlanningCentreButton } from '@/components/auth/PlanningCentreButton';
import { EmptyState, ErrorState } from '@/components/ui';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { errorMessage, isNetworkError, isNotFound, procedureErrorCode } from '@/lib/errors';
import { usePortalSettings } from '@/lib/settings';

/** Private procedure, nobody signed in: sign in and come straight back to /p/{code}. */
export function SignInRequired({ code }: { code: string }) {
  const { settings } = usePortalSettings();
  const back = `/p/${encodeURIComponent(code)}`;
  return (
    <div className="proc-main">
      <EmptyState
        icon={<IconLock size={28} />}
        title="Please sign in to open this procedure"
        action={<PlanningCentreButton redirectTo={back} primary />}
        secondaryAction={
          <Button
            component={Link}
            to={`/auth/login?redirect=${encodeURIComponent(back)}`}
            variant={settings.planningCentreLoginEnabled ? 'subtle' : 'filled'}
            leftSection={<IconLogin size={20} aria-hidden="true" />}
            fullWidth
          >
            {settings.planningCentreLoginEnabled ? 'Sign in with email instead' : 'Sign in'}
          </Button>
        }
      >
        This procedure is only for the team that looks after this equipment. Sign in once and we'll bring you straight back here.
      </EmptyState>
    </div>
  );
}

/** Signed in, but the procedure was not shared with this person. */
export function NotAssigned() {
  const { getUserDisplayName, logout } = useEnrichedAuth();
  const navigate = useNavigate();
  const name = getUserDisplayName();
  return (
    <div className="proc-main">
      <EmptyState
        icon={<IconUserOff size={28} />}
        title="This procedure has not been shared with you"
        action={<Button component={Link} to="/" fullWidth>See procedures you can open</Button>}
        secondaryAction={
          <Button
            variant="subtle"
            fullWidth
            onClick={async () => {
              try {
                await logout();
              } finally {
                navigate(`/auth/login?redirect=${encodeURIComponent(window.location.pathname)}`);
              }
            }}
          >
            Sign in as someone else
          </Button>
        }
      >
        {name ? `You're signed in as ${name}. ` : ''}If you need it, ask your team leader to add you.
      </EmptyState>
    </div>
  );
}

/** Unknown code, or a procedure that is not published (any more). */
export function ProcedureNotFound({ unpublished }: { unpublished?: boolean }) {
  return (
    <div className="proc-main">
      <EmptyState
        icon={<IconQrcode size={28} />}
        title={unpublished ? 'This procedure is not available right now' : "We couldn't find that procedure"}
        action={<Button component={Link} to="/" fullWidth>Find a procedure</Button>}
      >
        {unpublished
          ? 'It may be being updated. Please check with your team leader, or try again later.'
          : 'Please scan the QR code on the equipment again. If it still does not work, the code may be old.'}
      </EmptyState>
    </div>
  );
}

/**
 * Picks the right screen for a failed procedure load. Returns null when the error is not one of the
 * access cases (the caller shows its own retry).
 */
export function ProcedureAccessError({ code, error, onRetry, retrying }: { code: string; error: unknown; onRetry: () => void; retrying?: boolean }) {
  const c = procedureErrorCode(error);
  if (c === PROCEDURES_ERROR_CODES.SignInRequired) return <SignInRequired code={code} />;
  if (c === PROCEDURES_ERROR_CODES.NotAssigned) return <NotAssigned />;
  if (c === PROCEDURES_ERROR_CODES.NotPublished) return <ProcedureNotFound unpublished />;
  if (isNotFound(error)) return <ProcedureNotFound />;
  return (
    <div className="proc-main">
      <ErrorState
        title={isNetworkError(error) ? 'No signal' : "We couldn't open this procedure"}
        message={
          isNetworkError(error)
            ? "This procedure hasn't been opened on this phone before, so it needs signal the first time. Move to where you have signal and try again."
            : errorMessage(error)
        }
        onRetry={onRetry}
        retrying={retrying}
        secondaryAction={<Button component={Link} to="/" variant="default" fullWidth>All procedures</Button>}
      />
    </div>
  );
}
